import { t } from '../i18n'
import { sessionToken, signOut } from './auth'
import { remember } from './outbox'
import { handleRejection } from './session-state'

/**
 * Ручки аккаунта: выход, удаление, смена почты.
 * Все требуют ключ сессии; ответы сервера переводятся в коды, понятные экрану.
 */

import { apiBase } from './region'

export class AccountError extends Error {
  constructor(readonly code: string) {
    super(code)
  }
}

async function call(path: string, method: 'POST' | 'DELETE', body?: unknown): Promise<unknown> {
  const token = sessionToken()
  if (token === null) throw new AccountError('no_session')
  const response = await fetch(`${apiBase()}${path}`, {
    method,
    headers: { 'content-type': 'application/json', authorization: `Session ${token}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (response.status === 204) return null
  const json = (await response.json().catch(() => ({}))) as { error?: string }
  if (!response.ok) {
    await handleRejection(response.status)
    throw new AccountError(json.error ?? 'failed')
  }
  return json
}

/**
 * Тур показан. Без аккаунта рассказывать некому — тогда хватает записи на устройстве,
 * и молчание здесь не ошибка. Сеть тоже не повод шуметь: признак уже сохранён локально,
 * а сервер узнает при следующем обмене.
 */
export async function tellServerTourSeen(): Promise<void> {
  if (sessionToken() === null) return
  // Через очередь: без сети отметка полежит на устройстве и уйдёт позже,
  // иначе на втором телефоне тур начнётся заново.
  await remember('tour_seen')
}

/**
 * Сказать серверу, какой язык выбрал человек (решение заказчика 9).
 *
 * Язык — свойство человека, а не устройства: по нему приходят письма и говорит второй
 * телефон. Без аккаунта говорить некому, и это не ошибка: выбор лежит на устройстве.
 * Сбой сети тоже не беда — язык уедет со следующим выбором, а письма до тех пор пойдут
 * на языке устройства, с которого пришёл запрос.
 */
export async function tellServerLocale(lang: string): Promise<void> {
  if (sessionToken() === null) return
  try {
    await call('/account/locale', 'POST', { lang, updatedAt: Date.now() })
  } catch {
    // Молча: смена языка не должна падать из-за сети
  }
}

/** Выход на этом устройстве: сервер забывает сессию, приложение — данные (решение заказчика). */
export async function signOutEverywhereOnDevice(): Promise<void> {
  try {
    await call('/auth/session', 'DELETE')
  } catch (e) {
    // Нет связи — ключ всё равно забываем: держать его на устройстве незачем.
    // Условие было обратным, и выход без сети падал с ошибкой, оставляя человека
    // в аккаунте (разбор 12.10.2026). Наверх уходит только «а входа и не было».
    if (e instanceof AccountError && e.code === 'no_session') throw e
  }
  signOut()
}

/** Удаление аккаунта из приложения. Подтверждение спрашивает экран, здесь только запрос. */
export async function deleteAccount(): Promise<void> {
  await call('/account', 'DELETE')
  signOut()
}

/**
 * Убрать со счёта запись о малыше, в которой нет ни одной записи.
 *
 * Без аккаунта убирать нечего — это не ошибка. «has_records» значит, что на сервере
 * у записи есть история: устройство тогда тоже её не трогает.
 */
export async function forgetChild(clientId: string): Promise<'gone' | 'has_records' | 'failed'> {
  if (sessionToken() === null) return 'gone'
  try {
    await call(`/children/${encodeURIComponent(clientId)}`, 'DELETE')
    return 'gone'
  } catch (e) {
    return e instanceof AccountError && e.code === 'has_records' ? 'has_records' : 'failed'
  }
}

export interface EmailChange {
  requestId: string
}

/** Код уходит на новый адрес — так подтверждается, что он ваш. */
export async function requestEmailChange(email: string): Promise<EmailChange> {
  return (await call('/account/email-request', 'POST', { email })) as EmailChange
}

export async function confirmEmailChange(requestId: string, code: string): Promise<string> {
  const { email } = (await call('/account/email-confirm', 'POST', { requestId, code })) as { email: string }
  return email
}

/** Человеческие сообщения на коды сервера. */
export function accountMessage(e: unknown): string {
  const code = e instanceof AccountError ? e.code : 'failed'
  switch (code) {
    case 'taken':
      return t('error_email_taken')
    case 'same_email':
      return t('error_same_email')
    case 'bad_email':
      return t('error_bad_email')
    case 'wrong':
      return t('error_code_wrong')
    case 'expired':
      return t('error_code_expired')
    case 'used':
      return t('error_code_used')
    case 'too_soon':
      return t('error_too_soon')
    case 'too_many':
    case 'too_many_attempts':
      return t('error_too_many')
    case 'mail_failed':
      return t('error_mail_failed')
    case 'no_session':
      return t('error_no_session')
    default:
      return t('error_network')
  }
}
